import React, { FC, useCallback } from 'react';
import * as Styled from './ScoreOverview.styled';
import CoachingSessionReportGenerator from '@core/components/Analytics/VirtualHuman/ScoreOverview/components/CoachingSessionReportGenerator/CoachingSessionReportGenerator';
import ObjectiveGroupResult from '@core/components/Analytics/VirtualHuman/ScoreOverview/components/ObjectiveGroupResult/ObjectiveGroupResult';
import CoachingSessionReport from '@core/components/Analytics/VirtualHuman/ScoreOverview/components/CoachingSessionReport/CoachingSessionReport';
import { ToDecimalPlace } from '@base/utility/NumberFormatting';
import { clamp } from 'lodash';
import ScoreStats from '@core/components/Analytics/VirtualHuman/ScoreOverview/components/ScoreStats/ScoreStats';
import ProgressLoader from '@core/components/Loaders/ProgressLoader/ProgressLoader';
import { Button } from '@virtidev/toolbox';
import useGetVHHasEvaluations from '@base/utility/VHHooks/API/useGetVHHasEvaluations';
import LearnerFeedbackForm from '@core/components/Analytics/components/LearnerFeedbackForm/LearnerFeedbackForm';
import { useTranslation } from 'react-i18next';
import { useIsV3VH } from '@base/utility/VHHooks/useIsV3VH';

/**
 * @typedef {import('../../../../models/vh-server/vh-server-objective.types').VHServerObjectiveGroup} VHServerObjectiveGroup
 * @typedef {import('../../../../models/vh-server/vh-server-objective.types').VHServerObjective} VHServerObjective
 * @typedef {import('../../../../models/vh-server/vh-server-virtualhuman.types').VHServerVirtualHuman} VHServerVirtualHuman
 * @typedef {import('../../../../models/vh-server/vh-server-coaching-session-report.types').CoachingSessionReport} CoachingSessionReport
 * @typedef {import('../../../../models/vh-server/vh-server-coaching-session-report.types').CoachingSessionReportObjective} CoachingSessionReportObjective
 * @typedef {import('@core/models/virtualhuman.types').VirtualHuman} VirtualHuman
 * @typedef {import('@core/models/vh-server/vh-server-assessment.types').HitObjectives} HitObjectives
 *
 */

/**
 * @type { FC<{
 *    objectiveGroups?: VHServerObjectiveGroup[];
 *    allObjectives?: VHServerObjective[];
 *    timeSpent: number;
 *    vhDBVH:VHServerVirtualHuman;
 *    ssVH: VirtualHuman;
 *    hitObjectivesData?: HitObjectives;
 *    pointsScored: number;
 *    pointsPossible: number;
 *    hideScores: boolean;
 *    easyMode: boolean;
 *    shownHintObjectiveIds?: number[];
 *    footer?: React.ReactNode;
 *    sessionReport?: CoachingSessionReport & {objectiveReports: CoachingSessionReportObjective[];}
 *    showRegenerateButton?: boolean;
 *    generationProgress?: number;
 *    currentlyGeneratingReport?: boolean
 *    onRegenerate: () => void;
 *    allowedToGenerateReport?: boolean;
 *    allowLearnerFeedbackSetting: boolean;
 *    memberID?: string;
 *    learnerFeedbackRating?: number;
 *    analyticsSubmissionId?: number;
 *    createdDate: string | null;
 * }>}
 */
const ScoreOverview = ({
  objectiveGroups,
  allObjectives,
  timeSpent,
  onRegenerate,
  vhDBVH,
  ssVH,
  hitObjectivesData,
  pointsScored,
  pointsPossible,
  hideScores,
  easyMode,
  shownHintObjectiveIds,
  footer,
  sessionReport,
  showRegenerateButton,
  currentlyGeneratingReport,
  generationProgress,
  memberID,
  allowLearnerFeedbackSetting,
  learnerFeedbackRating,
  analyticsSubmissionId,
  createdDate,
}) => {
  const { t } = useTranslation();
  const generating =
    currentlyGeneratingReport ||
    (sessionReport && sessionReport.generatingStatus === 'pending')
      ? true
      : false;

  // const timeSuccess =
  //   timeSpent < ssVH.TimeLimit * 60 * 1000 || ssVH.TimeLimit === 0;
  let scorePercentageFraction = !pointsScored
    ? 0
    : pointsScored / pointsPossible;
  const isV3VH = useIsV3VH(ssVH.Type);
  if (isV3VH) {
    scorePercentageFraction = sessionReport?.score
      ? sessionReport?.score / 100
      : 0;
  }
  const clampedScorePercentageFraction = clamp(scorePercentageFraction, 0, 1);
  const scorePercentage = ToDecimalPlace(
    clampedScorePercentageFraction * 100,
    1
  );

  const handleRegenerateClick = useCallback(
    (e) => {
      e.stopPropagation();
      onRegenerate();
    },
    [onRegenerate]
  );

  const showScoreStats =
    (isV3VH && sessionReport?.generatingStatus === 'complete' && !generating) ||
    !isV3VH;

  const { hasEvaluations } = useGetVHHasEvaluations(vhDBVH.id);

  const generateButtonVisible =
    !sessionReport &&
    !generating &&
    ((['medical', 'default'].includes(ssVH.Type) && pointsPossible > 0) ||
      (isV3VH && hasEvaluations));

  const msToAllowBeforeRetry = 1000 * 60 * 1;
  const needReportGenerationRetry =
    !currentlyGeneratingReport &&
    (sessionReport?.generatingStatus === 'failed' ||
      (sessionReport?.generatingStatus === 'pending' &&
        new Date().getTime() - new Date(sessionReport.createdAt).getTime() >
          msToAllowBeforeRetry));
  return (
    <Styled.ScoreOverviewWrapper className="score-overview-wrapper">
      <Styled.ScoreOverviewContent className="score-overview-content">
        <div>
          {showScoreStats && (
            <Styled.MainSummary
              $vhType={ssVH.Type}
              $pointsPossible={pointsPossible}
              $disableDiagnosis={vhDBVH.disableDiagnosis}
            >
              <ScoreStats
                pointsPossible={pointsPossible}
                vhType={ssVH.Type}
                clampedScorePercentageFraction={clampedScorePercentageFraction}
                hideScores={hideScores}
                pointsScored={pointsScored}
                scorePercentage={scorePercentage}
                timeSpent={timeSpent}
              />
            </Styled.MainSummary>
          )}
          {/* conversation objectives results */}
          {objectiveGroups &&
            objectiveGroups.map((objectiveGroup) => (
              <ObjectiveGroupResult
                key={objectiveGroup.id}
                heading={objectiveGroup.name}
                allObjectives={allObjectives}
                hitObjectives={hitObjectivesData?.objectives ?? []}
                feedbackText={objectiveGroup.feedbackText ?? ''}
                easyMode={easyMode}
                shownHintObjectiveIds={shownHintObjectiveIds}
                groupId={objectiveGroup.id}
              />
            ))}
        </div>
        {generating && !needReportGenerationRetry && (
          <Styled.FakeProgressWrapper>
            <ProgressLoader
              progress={generationProgress ?? 0}
              text={t('analyzingPerformance')}
            />
          </Styled.FakeProgressWrapper>
        )}
        {needReportGenerationRetry && (
          <Styled.FailedWrapper>
            <Styled.FailedText>
              {t('lastReportGenerationFailed')}
            </Styled.FailedText>
            <Button
              design="outline"
              color="turquoise"
              onClick={handleRegenerateClick}
            >
              {t('retry')}
            </Button>
          </Styled.FailedWrapper>
        )}
        {generateButtonVisible && (
          <CoachingSessionReportGenerator
            loading={generating}
            onGenerateClick={handleRegenerateClick}
          />
        )}
        {sessionReport &&
          sessionReport.generatingStatus === 'complete' &&
          !generating && (
            <CoachingSessionReport
              showRegenerateButton={showRegenerateButton}
              report={sessionReport}
              onRegenerateClick={handleRegenerateClick}
            />
          )}
        {analyticsSubmissionId && memberID && (
          <Styled.LearnerFeedbackWrapper className="learner-feedback-wrapper">
            <LearnerFeedbackForm
              analyticsSubmissionId={analyticsSubmissionId}
              createdDate={createdDate}
              memberID={memberID}
              learnerFeedbackRating={learnerFeedbackRating}
              allowLearnerFeedbackSetting={allowLearnerFeedbackSetting}
            />
          </Styled.LearnerFeedbackWrapper>
        )}
      </Styled.ScoreOverviewContent>
      {footer && (
        <Styled.Footer className="score-overview-footer">
          {footer}
        </Styled.Footer>
      )}
    </Styled.ScoreOverviewWrapper>
  );
};

export default ScoreOverview;
